import React, { useCallback, useState } from 'react';
import ColorFillField from './ColorFillField';
import { Field, inputClass } from './BackgroundFields';
import {
  applyMediaDimensionsToSection,
  computeGridRowsFromAspectRatio,
} from '../utils/sectionGrid';
import {
  getSectionType,
  SECTION_TYPE_LABELS,
  DESKTOP_WIDTH,
  GRID_ROW_HEIGHT,
} from '../utils/defaults';
import { normalizeColorFill } from '../utils/colorFillUtils';
import { DEFAULT_IMAGE_OVERLAY } from '../utils/sectionOverlay';
import { coerceJardinsSectionGrid, gridRowHeightForSection } from '../utils/designKit';

export default function SectionBackgroundFields({ section, onChange }) {
  const sectionType = getSectionType(section);
  const bg = section.background || {};
  const [loadingMedia, setLoadingMedia] = useState(false);

  const colorFill = bg.fill ?? bg.color ?? '#ffffff';

  const patchBackground = (patch) => {
    onChange({
      ...section,
      background: { ...bg, ...patch },
      backgroundColor:
        patch.fill && typeof patch.fill === 'object' && patch.fill.mode === 'solid'
          ? patch.fill.color || section.backgroundColor
          : section.backgroundColor,
    });
  };

  const applyMediaUrl = useCallback(
    async (url, kind) => {
      if (!url?.trim()) return;
      setLoadingMedia(true);
      try {
        const base = {
          ...section,
          background: {
            ...bg,
            ...(kind === 'video' ? { type: 'video', videoUrl: url } : { type: 'image', imageUrl: url, imageSize: 'fill' }),
          },
        };
        const kitRowH = gridRowHeightForSection(section) || GRID_ROW_HEIGHT;
        const next = await applyMediaDimensionsToSection(base, url, kind, kitRowH);
        onChange(next);
      } finally {
        setLoadingMedia(false);
      }
    },
    [section, bg, onChange],
  );

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => applyMediaUrl(reader.result, 'image');
    reader.readAsDataURL(file);
  };

  const handleVideoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => applyMediaUrl(reader.result, 'video');
    reader.readAsDataURL(file);
  };

  const handlePosterFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => patchBackground({ posterUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const defaultRows =
    bg.mediaWidth && bg.mediaHeight
      ? computeGridRowsFromAspectRatio(bg.mediaWidth, bg.mediaHeight, DESKTOP_WIDTH)
      : 32;
  const desktopRows = Number(section.gridRows) > 0 ? Number(section.gridRows) : defaultRows;
  const approxHeightPx = Math.round(desktopRows * GRID_ROW_HEIGHT);

  return (
    <div className="space-y-4">
      {loadingMedia ? (
        <p className="text-xs text-muted-foreground">Calcul de la hauteur depuis le média…</p>
      ) : null}

      <Field label="Type de section">
        <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm text-foreground">
          {SECTION_TYPE_LABELS[sectionType] || sectionType}
        </div>
      </Field>

      {sectionType === 'normal' ? (
        <ColorFillField
          label="Couleur ou dégradé"
          value={colorFill}
          onChange={(fill) =>
            patchBackground({
              type: 'color',
              fill: normalizeColorFill(fill),
              color: undefined,
            })
          }
          fallback="#ffffff"
        />
      ) : null}

      {(sectionType === 'image' ||
        sectionType === 'video' ||
        sectionType === 'normal' ||
        section?.designKit === 'jardins') && (
        <>
          <Field label="Hauteur de la section (lignes grille)">
            <input
              type="number"
              min={4}
              max={600}
              className={inputClass}
              value={desktopRows}
              onChange={(e) => {
                const patched = {
                  ...section,
                  gridRows: Math.max(4, Math.min(600, Number(e.target.value) || defaultRows)),
                  gridRowsCustom: true,
                };
                onChange(
                  section?.designKit === 'jardins'
                    ? coerceJardinsSectionGrid(patched, { syncBleedImage: true })
                    : patched,
                );
              }}
            />
          </Field>
          <p className="text-xs text-muted-foreground -mt-2">
            ≈ {approxHeightPx}px en largeur desktop
            {sectionType === 'image' || sectionType === 'video'
              ? ' — le média remplit la zone (fill).'
              : ' — ou glissez la poignée en bas de la section sélectionnée.'}
          </p>
        </>
      )}

      {sectionType === 'image' ? (
        <>
          <ColorFillField
            label="Couleur sous l'image"
            value={colorFill}
            onChange={(fill) =>
              patchBackground({ fill: normalizeColorFill(fill), color: undefined })
            }
            fallback="#ffffff"
          />
          <Field label="Image (URL)">
            <input
              className={inputClass}
              value={bg.imageUrl || ''}
              onChange={(e) =>
                patchBackground({
                  type: 'image',
                  imageUrl: e.target.value,
                  imageSize: 'fill',
                })
              }
              onBlur={(e) => applyMediaUrl(e.target.value, 'image')}
              placeholder="https://..."
            />
          </Field>
          <Field label="Importer une image">
            <input type="file" accept="image/*" onChange={handleImageFile} className="text-sm" />
          </Field>
          <Field label="Cadrage (object-position)">
            <input
              className={inputClass}
              value={bg.imagePosition || 'center'}
              onChange={(e) => patchBackground({ imagePosition: e.target.value })}
              placeholder="center 53%"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={bg.overlayEnabled !== false && Boolean(bg.overlay)}
              onChange={(e) => {
                if (!e.target.checked) {
                  patchBackground({ overlayEnabled: false });
                  return;
                }
                patchBackground({
                  overlayEnabled: true,
                  overlay: bg.overlay || DEFAULT_IMAGE_OVERLAY,
                });
              }}
            />
            Voile / griseur sur l&apos;image (alpha)
          </label>
          {bg.overlayEnabled !== false && bg.overlay ? (
            <>
              <ColorFillField
                label="Couleur du voile"
                value={bg.overlay}
                onChange={(fill) =>
                  patchBackground({
                    overlayEnabled: true,
                    overlay: normalizeColorFill(fill),
                  })
                }
                fallback="rgba(13,28,20,0.6)"
              />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={bg.overlayFooter === true}
                  onChange={(e) => patchBackground({ overlayFooter: e.target.checked })}
                />
                Dégradé bas (effet hero)
              </label>
            </>
          ) : null}
          <p className="text-xs text-muted-foreground">
            La hauteur s&apos;adapte au ratio de l&apos;image à l&apos;import. Ajustez ensuite
            manuellement si besoin.
          </p>
        </>
      ) : null}

      {sectionType === 'video' ? (
        <>
          <ColorFillField
            label="Couleur sous la vidéo"
            value={colorFill}
            onChange={(fill) =>
              patchBackground({ fill: normalizeColorFill(fill), color: undefined })
            }
            fallback="#000000"
          />
          <Field label="Vidéo (URL)">
            <input
              className={inputClass}
              value={bg.videoUrl || ''}
              onChange={(e) => patchBackground({ type: 'video', videoUrl: e.target.value })}
              onBlur={(e) => applyMediaUrl(e.target.value, 'video')}
              placeholder="https://..."
            />
          </Field>
          <Field label="Importer une vidéo">
            <input type="file" accept="video/*" onChange={handleVideoFile} className="text-sm" />
          </Field>
          <Field label="Image de prévisualisation (URL)">
            <input
              className={inputClass}
              value={bg.posterUrl || ''}
              onChange={(e) => patchBackground({ posterUrl: e.target.value })}
              placeholder="https://..."
            />
          </Field>
          <Field label="Importer une prévisualisation">
            <input type="file" accept="image/*" onChange={handlePosterFile} className="text-sm" />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={bg.overlayEnabled !== false && Boolean(bg.overlay)}
              onChange={(e) => {
                if (!e.target.checked) {
                  patchBackground({ overlayEnabled: false });
                  return;
                }
                patchBackground({
                  overlayEnabled: true,
                  overlay: bg.overlay || DEFAULT_IMAGE_OVERLAY,
                });
              }}
            />
            Voile / griseur sur la vidéo (alpha)
          </label>
          {bg.overlayEnabled !== false && bg.overlay ? (
            <ColorFillField
              label="Couleur du voile"
              value={bg.overlay}
              onChange={(fill) =>
                patchBackground({
                  overlayEnabled: true,
                  overlay: normalizeColorFill(fill),
                })
              }
              fallback="rgba(13,28,20,0.6)"
            />
          ) : null}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={bg.muted !== false}
              onChange={(e) => patchBackground({ muted: e.target.checked })}
            />
            Lecture silencieuse
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={bg.loop !== false}
              onChange={(e) => patchBackground({ loop: e.target.checked })}
            />
            Lecture en boucle
          </label>
        </>
      ) : null}
    </div>
  );
}
